import React from 'react';
import { Field, FieldProps } from 'formik';
import { tv } from 'tailwind-variants';
import { FormFieldWrapper } from './FormFieldWrapper';
import { CustomForm } from '.generated/Feature.Irwinmitchell.model';
import { IconTypes, SvgIcon, SvgIconSize } from 'helpers/SvgIcon';

const formFieldStyles = tv({
  slots: {
    base: [
      'w-full',
      'min-h-12',
      'px-spacing-spacing-16',
      'py-spacing-spacing-12',
      'border',
      'border-color-input-border',
      'rounded-[8px]',
      'bg-color-grayscale-white',
      'placeholder:text-color-text-text-secondary',
      'text-color-text-text',
      'hover:border-color-input-border-hover',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'placeholder:italic',
      'font-normal',
      'disabled:text-color-text-text-disabled',
      'disabled:cursor-not-allowed',
      'focus:outline-none',
      'focus:ring-1',
      'focus:border-color-input-border-focused',
      'focus:ring-color-input-border-focused',
      'focus:ring-shadow-[0_0_0_1px_var(--color-core-color-4-SkyBlue)]',
    ],
    icon: ['absolute', 'left-[18px]', 'top-[18px]'],
    fieldWrapper: ['relative'],
  },
  variants: {
    state: {
      default: {
        base: [],
      },
      error: {
        base: [
          'border-color-text-text-critical',
          'focus:border-color-text-text-error',
          'bg-color-fill-error-light',
        ],
      },
      disabled: {
        base: ['bg-color-grayscale-gray-100', 'cursor-not-allowed'],
      },
    },
    hasIcon: {
      true: {
        base: ['pl-[30px]'],
      },
      false: {
        base: ['pl-spacing-spacing-16'],
      },
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

interface NumberFieldProps {
  field: CustomForm.FormField_Item;
  hasError?: boolean;
  isTouched?: boolean;
  fieldIcon?: IconTypes;
  fieldIconSize?: SvgIconSize;
  maxDigits?: number;
}

export const NumberField: React.FC<NumberFieldProps> = ({
  field,
  hasError = false,
  fieldIcon,
  fieldIconSize,
  maxDigits,
}) => {
  const fieldConfig = field.fields;
  const fieldName = fieldConfig?.fieldName?.value || fieldConfig?.fieldId?.value;
  const label = fieldConfig?.label?.value || fieldName || '';
  const helpText = fieldConfig?.helpText?.value;
  const disabled = fieldConfig?.disabled?.value;
  const required = fieldConfig?.required?.value;
  const placeholder = fieldConfig?.placeholderText?.value;
  const fieldState = disabled ? 'disabled' : hasError ? 'error' : 'default';
  const { base, icon, fieldWrapper } = formFieldStyles({ state: fieldState, hasIcon: !!fieldIcon });

  if (!fieldName) return null;

  const commonFieldProps = {
    name: fieldName,
    id: fieldName,
    disabled,
    className: base(),
    'data-automation': `numberField-${fieldName}`,
  };

  return (
    <FormFieldWrapper
      key={fieldName}
      fieldName={fieldName}
      label={label}
      helpText={helpText}
      isDisabled={disabled}
      hasError={hasError}
      isRequired={required}
    >
      <div className={fieldWrapper()}>
        {fieldIcon && <SvgIcon className={icon()} icon={fieldIcon} size={fieldIconSize} />}
        <Field name={fieldName}>
          {({ field, form }: FieldProps) => {
            const rawValue = form?.values?.[fieldName] ?? '';
            const stringValue = String(rawValue);

            // Format the value: allow digits and one decimal point
            const formatValue = (value: string): string => {
              if (!value || value === '') return '';

              // Remove commas first, then remove all characters except digits and decimal point
              let cleaned = value.replace(/,/g, '').replace(/[^\d.]/g, '');

              if (!cleaned) return '';

              // Ensure only one decimal point
              const parts = cleaned.split('.');
              if (parts.length > 2) {
                cleaned = parts[0] + '.' + parts.slice(1).join('');
              }

              // Limit decimal places to 4
              if (parts.length === 2 && parts[1].length > 4) {
                cleaned = parts[0] + '.' + parts[1].slice(0, 4);
              }

              // Format integer part with commas
              if (cleaned.includes('.')) {
                const [integerPart, decimalPart] = cleaned.split('.');
                const formattedInteger =
                  integerPart === '' ? '' : integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                return `${formattedInteger}.${decimalPart}`;
              } else {
                return cleaned.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
              }
            };

            const formattedValue = formatValue(stringValue);

            const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
              const input = e.target.value;
              // Remove commas and all characters except digits and decimal point
              let cleaned = input.replace(/,/g, '').replace(/[^\d.]/g, '');

              // Ensure only one decimal point
              const parts = cleaned.split('.');
              if (parts.length > 2) {
                cleaned = parts[0] + '.' + parts.slice(1).join('');
              }

              // Limit decimal places to 4
              if (parts.length === 2 && parts[1].length > 4) {
                cleaned = parts[0] + '.' + parts[1].slice(0, 4);
              }

              // Limit integer part to maxDigits if specified
              if (maxDigits) {
                const [integerPart, decimalPart] = cleaned.split('.');
                if (integerPart && integerPart.length > maxDigits) {
                  cleaned =
                    integerPart.slice(0, maxDigits) + (decimalPart ? '.' + decimalPart : '');
                }
              }

              form.setFieldValue(fieldName, cleaned);
            };

            const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
              // Prevent entering dash/minus
              if (e.key === '-' || e.key === 'Minus') {
                e.preventDefault();
              }
              // Allow decimal point if not already present
              if (e.key === '.' || e.key === 'Decimal') {
                const currentValue = String(form?.values?.[fieldName] ?? '');
                // Check raw value (without commas) for existing decimal point
                if (currentValue.replace(/,/g, '').includes('.')) {
                  e.preventDefault();
                }
              }
            };

            return (
              <input
                type="text"
                inputMode="decimal"
                pattern="[0-9]*\.?[0-9]{0,4}"
                placeholder={placeholder}
                aria-required={required}
                {...commonFieldProps}
                name={field.name}
                id={field.name}
                value={formattedValue}
                onChange={handleChange}
                onBlur={field.onBlur}
                onKeyDown={handleKeyDown}
              />
            );
          }}
        </Field>
      </div>
    </FormFieldWrapper>
  );
};

export default NumberField;
